import { useEffect, useRef } from "react";
import { useHighlightsApi } from "src/api/merchflows/highlights";
import { Page, Planogram } from "src/components";
import { useToasts } from "src/components/Toasts";
import { Button, Color, Flex } from "src/elements";

import { usePlayground } from "./store/atoms";

import { useLoaders } from "src/components/InlineLoader";
import { PlanogramMetrics } from "src/components/PlanogramExtensions/PlanogramMetrics";
import { PlanogramHovererDetails } from "src/components/PlanogramExtensions/PlanogramHovererDetails/PlanogramHovererDetails";
import { useApi } from "src/api";
import { RoutePutMerchflowStorePog_storePogId_ } from "@CommonApi/merchflow/store/pog/_storePogId_";
import { Pog } from "@CommonTypes/merchflow/pog/pog";
import { RoutePostMerchflows_merchflowId_ExportsBatch } from "@CommonApi/merchflows/_merchflowId_/exports/batch";
import { PlanogramToolbar } from "src/components/Planogram/components/Toolbar/PlanogramToolbar";

const merchflowId = 3905;
const storePogId = 13896;

export const PagePlayground = () => {
  const { getBaseHighlightsApi } = useHighlightsApi();
  const { putMerchflowStorePogApi, postExportBatchPogsApi } = useApi();
  const { hasLoader } = useLoaders();
  const { pogAtom, pog, setPog } = usePlayground();
  const { toast } = useToasts();

  const refFile = useRef<HTMLInputElement>(null);

  const openFileUpload = () => {
    refFile.current?.click();
  };

  const onFileUpload = async (file: File) => {
    try {
      setPog(new Pog(JSON.parse(await file.text())));
    } catch (error) {
      toast({
        title: "Invalid POG file",
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        error,
      });
    }
  };

  useEffect(() => {
    getBaseHighlightsApi(1);
  }, []);

  const onExport = async () => {
    if (pog) {
      await putMerchflowStorePogApi({
        storePogId,
        payload: { pog: pog.toNonCircular() },
      });
      await postExportBatchPogsApi(merchflowId, {
        store_pogs_ids: [storePogId],
        type: "psaV2",
      });
    }
  };

  return (
    <Page>
      <Flex direction="column" borderColor={Color.lightGrayHover} borderRadius="4px">
        <Flex justify="right" padding="10px">
          <Button onClick={openFileUpload}>Upload POG</Button>
        </Flex>
      </Flex>

      {pog !== null && (
        <>
          <Flex width="100%" direction="column" gap="10px" flexGrow={1} minHeight="0">
            <PlanogramToolbar pogAtom={pogAtom} />

            <Planogram
              pogAtom={pogAtom}
              isEditable
              isPog={pog.containers.length === 0}
              isPanelVisible
              merchflowId={merchflowId}
              isWithBorder
            />

            <PlanogramHovererDetails />
          </Flex>

          <Flex justify="between" align="center">
            <PlanogramMetrics pogAtom={pogAtom} />

            <Flex gap="10px">
              <Button
                onClick={onExport}
                isLoading={
                  hasLoader(RoutePostMerchflows_merchflowId_ExportsBatch) ||
                  hasLoader(RoutePutMerchflowStorePog_storePogId_)
                }
              >
                Export
              </Button>
            </Flex>
          </Flex>
        </>
      )}

      <input
        ref={refFile}
        type="file"
        accept="application/json"
        style={{ display: "none" }}
        onChange={(event) => {
          if (event.target.files !== null && event.target.files.length > 0) {
            onFileUpload(event.target.files[0]);
          }

          // Clear input to be able to reupload same file.
          event.target.value = "";
          event.target.files = null;
        }}
      />
    </Page>
  );
};
